import React, { useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Button,
  Collapse,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Business as BusinessIcon,
  Computer as ComputerIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Apps as AppsIcon,
  Notifications as NotificationsIcon,
  Description as DescriptionIcon,
  Replay as ReplayIcon,
  Schedule as ScheduleIcon,
  AccountBalance as AccountBalanceIcon,
  Domain as DomainIcon,
  People as PeopleIcon,
  Menu as MenuIcon,
  Brightness4 as Brightness4Icon,
  Brightness7 as Brightness7Icon,
  ExpandLess,
  ExpandMore,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useFilter } from '../contexts/FilterContext';
import TopFilterBar from './TopFilterBar';
import { useThemeMode } from '../contexts/ThemeModeContext';

import ehesappLogo from '../ehesapp-logo.png';
import ehesappLogoWhite from '../ehesapp_logo_white.png';

const drawerWidth = 280;
const DRAWER_STORAGE_KEY = 'monitoring-drawer-open';

const getInitialDrawerOpen = (): boolean => {
  try {
    return sessionStorage.getItem(DRAWER_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
};

interface LayoutProps {
  children: ReactNode;
}

interface MenuItemChild {
  text: string;
  path: string;
  icon?: React.ReactNode;
}

interface MenuItem {
  text: string;
  icon?: React.ReactNode;
  path: string;
  children?: MenuItemChild[];
}

const toolbarHeight = { xs: 56, sm: 64 };

const Layout = ({ children }: LayoutProps) => {
  const { user, logout } = useAuth();
  const { triggerRefresh, autoRefresh, setAutoRefresh } = useFilter();
  const { mode, toggleMode } = useThemeMode();
  const theme = useTheme();
  const isSmUp = useMediaQuery(theme.breakpoints.up('sm'));
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpenState] = useState(getInitialDrawerOpen);
  const [openMenus, setOpenMenus] = useState<Record<string, boolean>>({});

  const setDrawerOpen = (value: boolean | ((prev: boolean) => boolean)) => {
    setDrawerOpenState((prev) => {
      const next = typeof value === 'function' ? value(prev) : value;
      try {
        sessionStorage.setItem(DRAWER_STORAGE_KEY, String(next));
      } catch {
        // ignore
      }
      return next;
    });
  };

  const handleDrawerToggle = () => setDrawerOpen((o) => !o);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const menuItems: MenuItem[] = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
    { text: 'Şirket Grupları', icon: <BusinessIcon />, path: '/tenants' },
    { text: 'Sunucular', icon: <ComputerIcon />, path: '/servers' },
    { text: "Agent'lar", icon: <SettingsIcon />, path: '/agents' },
    { text: 'Uygulama Tanımları', icon: <AppsIcon />, path: '/application-definitions' },
    {
      text: 'eStatement',
      icon: <DescriptionIcon />,
      path: '/estatement',
      children: [
        { text: 'Banka Servis Tetikleme', path: '/estatement/banka-servis-tetikleme', icon: <ReplayIcon /> },
        { text: 'Son İşlem Zamanı', path: '/estatement/son-islem-zamani', icon: <ScheduleIcon /> },
        { text: 'Şirketler', path: '/estatement/sirketler', icon: <DomainIcon /> },
        { text: 'Banka Tanımları', path: '/estatement/bankalar', icon: <AccountBalanceIcon /> },
      ],
    },
    { text: 'Hatalar', icon: <NotificationsIcon />, path: '/alerts' },
    { text: 'Kullanıcı Tanımları', icon: <PeopleIcon />, path: '/users' },
  ];

  const handleMenuClick = (item: MenuItem) => {
    if (item.children && item.children.length > 0) {
      setOpenMenus(prev => ({
        ...prev,
        [item.path]: !prev[item.path]
      }));
    } else {
      navigate(item.path);
    }
  };

  const isMenuOpen = (item: MenuItem) => {
    return openMenus[item.path] || (item.children?.some(child => location.pathname.startsWith(child.path)) ?? false);
  };

  const renderMenuItem = (item: MenuItem) => {
    const hasChildren = item.children && item.children.length > 0;
    const isOpen = isMenuOpen(item);
    const isSelected = location.pathname === item.path || 
      (hasChildren && item.children?.some(child => location.pathname === child.path));

    return (
      <React.Fragment key={item.path}>
        <ListItem disablePadding>
          <ListItemButton
            selected={isSelected}
            onClick={() => handleMenuClick(item)}
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText primary={item.text} />
            {hasChildren && (isOpen ? <ExpandLess /> : <ExpandMore />)}
          </ListItemButton>
        </ListItem>
        {hasChildren && (
          <Collapse in={isOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.children?.map((child) => (
                <ListItem key={child.path} disablePadding>
                  <ListItemButton
                    selected={location.pathname === child.path}
                    onClick={() => navigate(child.path)}
                    sx={{ pl: 4 }}
                  >
                    {child.icon && (
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        {child.icon}
                      </ListItemIcon>
                    )}
                    <ListItemText primary={child.text} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Collapse>
        )}
      </React.Fragment>
    );
  };

  return (
    <Box sx={{ display: 'flex', width: '100%', overflowX: 'hidden' }}>
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          bgcolor: 'background.paper',
          color: 'text.primary',
          boxShadow: 1,
        }}
      >
        <Toolbar
          sx={{
            minHeight: toolbarHeight,
            py: { xs: 0.5, sm: 0 },
            flexWrap: 'nowrap',
            gap: 0.5,
          }}
        >
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 1.5 }}
            aria-label="Menüyü aç/kapat"
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexGrow: 1, minWidth: 0 }}>
            <Box
              component="img"
              src={mode === 'dark' ? ehesappLogoWhite : ehesappLogo}
              alt="eHesApp"
              sx={{ height: 36, width: 'auto', objectFit: 'contain', display: { xs: 'none', sm: 'block' } }}
            />
            <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              Monitoring Sistemi
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 }, flexWrap: 'nowrap', minWidth: 0 }}>
            <Tooltip title={mode === 'light' ? 'Karanlık mod' : 'Aydınlık mod'}>
              <IconButton color="inherit" onClick={toggleMode} aria-label={mode === 'light' ? 'Karanlık moda geç' : 'Aydınlık moda geç'}>
                {mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
              </IconButton>
            </Tooltip>
            <TopFilterBar
              onRefresh={triggerRefresh}
              autoRefresh={autoRefresh}
              onAutoRefreshChange={setAutoRefresh}
            />
            <Typography variant="body2" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
              {user?.username} ({user?.role})
            </Typography>
            <Tooltip title="Çıkış">
              <Button
                color="inherit"
                startIcon={<LogoutIcon />}
                onClick={handleLogout}
                sx={{ color: 'text.primary', minWidth: { xs: 40, sm: 'auto' }, px: { xs: 1, sm: 2 } }}
                aria-label="Çıkış"
              >
                {isSmUp ? 'Çıkış' : null}
              </Button>
            </Tooltip>
          </Box>
        </Toolbar>
      </AppBar>
      <Drawer
        variant="persistent"
        open={drawerOpen}
        sx={{
          '& .MuiDrawer-paper': {
            width: drawerOpen ? { xs: 'min(100vw, 280px)', sm: drawerWidth } : 0,
            boxSizing: 'border-box',
            top: { xs: 56, sm: 64 },
            height: { xs: 'calc(100% - 56px)', sm: 'calc(100% - 64px)' },
            overflowX: 'hidden',
            transition: (theme) => theme.transitions.create('width', { easing: theme.transitions.easing.sharp, duration: theme.transitions.duration.enteringScreen }),
            borderRight: '1px solid',
            borderColor: 'divider',
          },
        }}
      >
        <Box sx={{ overflow: 'auto', py: 2, width: drawerWidth }}>
          <Box sx={{ px: 2, pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
            <img src={mode === 'dark' ? ehesappLogoWhite : ehesappLogo} alt="eHesApp Logo" style={{ maxHeight: 48, width: 'auto', objectFit: 'contain', display: 'block' }} />
          </Box>
          <List sx={{ pt: 1 }}>
            {menuItems.map((item) => renderMenuItem(item))}
          </List>
        </Box>
      </Drawer>
      {/* Drawer açıkken solda boşluk bırakır; ana içerik sağda kalan alanı doldurur */}
      <Box
        sx={{
          width: drawerOpen ? { xs: 'min(100vw, 280px)', sm: drawerWidth } : 0,
          flexShrink: 0,
          transition: (theme) => theme.transitions.create('width', { easing: theme.transitions.easing.sharp, duration: theme.transitions.duration.enteringScreen }),
        }}
      />
      <Box
        component="main"
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-start',
          bgcolor: 'background.default',
          p: { xs: 2, sm: 3 },
          minHeight: '100vh',
          overflow: 'auto',
        }}
      >
        <Toolbar />
        <Box sx={{ width: '100%' }}>{children}</Box>
      </Box>
    </Box>
  );
};

export default Layout;

